import { RequestApiPrivate } from "../../component/api/axios";
import { downloadFile } from "../../../../api/MediaApi";
import { filtersToParams, type TicketRequestsFilters } from "./RequestReportApi";

export interface ProcessDropdownVersion {
  id: number;
  version: string;
}

export interface ProcessDropdownItem {
  id: number;
  name: string;
  code: string;
  owner_workspace_id: number;
  version: string;
  versions: ProcessDropdownVersion[];
}

export interface ProcessDropdownListResponse {
  list: ProcessDropdownItem[];
  page: number;
  limit: number;
  totalItem: number;
}

export interface GetProcessDropdownListParams {
  name?: string;
  page?: number;
  workspaceIds: number[];
}

export async function getProcessDropdownList(
  params: GetProcessDropdownListParams
): Promise<ProcessDropdownListResponse> {
  // BE rejects an empty-string `name` (422 validation.string) — omit the param
  // entirely when there's no search term, so BE treats it as "no filter".
  // POST (not GET) because workspace_ids can be long enough to overflow a URL.
  const { data } = await RequestApiPrivate.post("process-reports/processes", {
    name: params.name || undefined,
    page: params.page,
    workspace_ids: params.workspaceIds,
  });
  return data.data;
}

export interface GetReportProcessDropdownListParams {
  name?: string;
  page?: number;
}

export async function getRequestReportProcessDropdownList(
  params: GetReportProcessDropdownListParams
): Promise<ProcessDropdownListResponse> {
  const { data } = await RequestApiPrivate.post("process-reports/request-report-processes", {
    name: params.name || undefined,
    page: params.page,
  });
  return data.data;
}

export async function getMissionReportProcessDropdownList(
  params: GetReportProcessDropdownListParams
): Promise<ProcessDropdownListResponse> {
  const { data } = await RequestApiPrivate.post("process-reports/mission-report-processes", {
    name: params.name || undefined,
    page: params.page,
  });
  return data.data;
}

export interface ExportFileInfo {
  disk: string;
  path: string;
  name: string;
  size: number | null;
  mime_type: string;
}

export interface ExportStatusResponse {
  id: number;
  type: string;
  status: 1 | 2 | 3 | 4 | 5 | 6;
  upload_status: number | null;
  request_id: string;
  file_content: string | null; // base64, present only on the first status check after COMPLETED
  file_info: ExportFileInfo | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export async function createTicketExportList(filters: TicketRequestsFilters): Promise<ExportStatusResponse> {
  const { data } = await RequestApiPrivate.post(
    "process-reports/ticket-request/export-list",
    filtersToParams(filters)
  );
  return data.data;
}

export interface CreateTicketExportParams {
  processIds: number[];
  startDate: string; // "yyyy-MM-dd HH:mm:ss"
  endDate: string;   // "yyyy-MM-dd HH:mm:ss"
}

export async function createTicketExport(params: CreateTicketExportParams): Promise<ExportStatusResponse> {
  const { data } = await RequestApiPrivate.post("process-reports/ticket-request/export", {
    process_ids: params.processIds,
    start_date: params.startDate,
    end_date: params.endDate,
  });
  return data.data;
}

export interface GetTicketRequestExportCountParams {
  processIds: number[];
  startDate: string; // "yyyy-MM-dd HH:mm:ss"
  endDate: string;   // "yyyy-MM-dd HH:mm:ss"
}

export async function getTicketRequestExportCount(
  params: GetTicketRequestExportCountParams
): Promise<number> {
  const { data } = await RequestApiPrivate.post("process-reports/ticket-request/export-count", {
    process_ids: params.processIds,
    start_date: params.startDate,
    end_date: params.endDate,
  });
  return data.data.count;
}

export async function getExportStatus(id: number): Promise<ExportStatusResponse> {
  const { data } = await RequestApiPrivate.get(`process-reports/${id}`);
  return data.data;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

// The completed export's file content is embedded (base64) directly in the status
// response — there's no separate download endpoint/URL. It's only present on the
// first status check after COMPLETED (the backend deletes its local copy once read),
// so a stale/repeated status check yields `file_content: null` even though `status`
// still reads COMPLETED.
export async function downloadExportFile(response: ExportStatusResponse): Promise<void> {
  if (!response.file_content) {
    throw new Error("Missing file_content");
  }
  const blob = base64ToBlob(
    response.file_content,
    response.file_info?.mime_type ?? "application/octet-stream"
  );
  downloadFile({ data: blob, headers: {} }, response.file_info?.name);
}
